import React, { useEffect, useState, createElement, Component, isValidElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, MapPin, Mail, Phone, ArrowRight, MessageSquare, ShieldCheck, Clock, Search } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { NextPageLink } from '../components/NextPageLink';
import { useSEO } from '../utils/seo';
// --- Types & Data ---
interface FAQItem {
  question: string;
  answer: React.ReactNode;
}
interface FAQCategory {
  title: string;
  items: FAQItem[];
}
const faqData: FAQCategory[] = [{
  title: 'Fonctionnement du laser',
  items: [{
    question: 'Comment le traitement laser agit-il ?',
    answer: <div className="space-y-4" data-id="element-590">
            <p className="font-bold text-brand-light" data-id="element-591">
              Fonctionnement du détatouage au laser
            </p>
            <p data-id="element-592">
              Le laser de détatouage pénètre dans la peau et cible
              spécifiquement les particules d'encre de tatouage. L'énergie
              lumineuse est absorbée par les pigments, qui sont chauffés puis
              fragmentés en particules microscopiques. Dans les semaines / mois
              qui suivent la séance, le système immunitaire élimine
              naturellement ces fragments d'encre.
            </p>
            <p data-id="element-593">
              Lors des séances suivantes, le laser agit sur des couches de peau
              plus profondes afin de fragmenter les pigments restants. Ce
              processus se répète jusqu'à ce que l'encre soit suffisamment
              décomposée pour être éliminée par l'organisme, permettant ainsi
              d'effacer le tatouage progressivement et naturellement.
            </p>
          </div>
  }, {
    question: 'Détatouage Martigny : Quels lasers sont utilisés pour la procédure ?',
    answer: <p data-id="element-594">
            Chez dispel your tattoo à Martigny, les deux technologies sont
            utilisées et combinées pour obtenir les meilleurs résultats possible
            : picoseconde et nanoseconde lasers. En plus des deux lasers, un
            appareil cryo qui souffle de l'air froid à -30 degrés est utilisé
            pour rendre la procédure plus confortable.
          </p>
  }, {
    question: 'Détatouage Martigny : peut-on enlever toutes les couleurs ?',
    answer: <div className="space-y-4" data-id="element-595">
            <p className="font-bold text-brand-light" data-id="element-596">
              Couleurs faciles et couleurs plus complexes
            </p>
            <p data-id="element-597">
              Le détatouage au laser permet d'effacer la majorité des couleurs
              de tatouage. Les couleurs foncées comme le noir et le brun
              réagissent plus rapidement au laser.
            </p>
            <p data-id="element-598">
              Les couleurs claires telles que le rose, le jaune ou le turquoise
              sont plus difficiles à éliminer avec tout les lasers, car elles
              réfléchissent davantage la lumière.
            </p>
            <p data-id="element-599">
              Le blanc ne s'efface pas avec tous les lasers car il réfléchit la
              lumière. Sous l'effet du laser, le dioxyde de titane qu'il
              contient peut se transformer et changer de couleur, ce qui le rend
              ensuite plus complexe à effacer.
            </p>
            <p data-id="element-600">
              Le bleu et le vert nécessitent des lasers spéciaux utilisant
              d'autres longueurs d'onde (755/694nm)
            </p>
            <p className="italic text-brand-gray/80 mt-4" data-id="element-601">
              Si vous souhaitez savoir exactement quelles couleurs peuvent être
              enlevées sur votre tatouage, n'hésitez pas à nous contacter via le
              formulaire de contact.
            </p>
          </div>
  }]
}, {
  title: 'Séances et résultats',
  items: [{
    question: 'Combien de séances sont nécessaires pour effacer un tatouage ?',
    answer: <div className="space-y-4" data-id="element-602">
            <p className="font-bold text-brand-light" data-id="element-603">
              Facteurs influençant le nombre de séances
            </p>
            <p data-id="element-604">
              Le nombre de séances de détatouage dépend de plusieurs éléments :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-605">
              <li data-id="element-606">la profondeur et la densité de l'encre</li>
              <li data-id="element-607">les couleurs utilisées</li>
              <li data-id="element-608">l'emplacement du tatouage</li>
              <li data-id="element-609">le type de peau</li>
              <li data-id="element-610">le système lymphatique</li>
            </ul>
            <p className="mt-4" data-id="element-611">En moyenne :</p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-612">
              <li data-id="element-613">tatouages peu dense : 3 à 6 séances</li>
              <li data-id="element-614">tatouages plus complexes : 8 à 12 séances ou davantage</li>
            </ul>
            <p className="mt-4" data-id="element-615">
              Un délai de 6 à 8 semaines entre chaque séance est indispensable.
              À Martigny, un délai minimum de 50 jours est respecté afin de
              garantir une bonne cicatrisation et une élimination efficace des
              pigments.
            </p>
          </div>
  }, {
    question: 'Combien de temps attendre entre chaque séance de détatouage ?',
    answer: <div className="space-y-4" data-id="element-616">
            <p className="font-bold text-brand-light" data-id="element-617">
              Un délai essentiel pour la peau et le système immunitaire
            </p>
            <p data-id="element-618">
              Il est généralement recommandé d'attendre entre 6 et 8 semaines
              entre chaque séance de détatouage laser. Ce délai n'est pas
              arbitraire : il est indispensable pour permettre à la peau de se
              réparer correctement et au système immunitaire de faire son
              travail.
            </p>
            <p data-id="element-619">
              À Martigny, un délai minimum de 50 jours est respecté entre chaque
              séance. Ce temps permet :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-620">
              <li data-id="element-621">une cicatrisation complète de la peau</li>
              <li data-id="element-622">une diminution du risque d'effets secondaires</li>
              <li data-id="element-623">
                une élimination naturelle et progressive des pigments fragmentés
              </li>
            </ul>
            <p className="mt-4" data-id="element-624">
              Même si l'envie d'aller plus vite est compréhensible, respecter ce
              délai est l'un des éléments clés pour un détatouage sans cicatrice
              et des résultats durables.
            </p>
          </div>
  }, {
    question: 'Un grand tatouage est-il plus difficile à enlever ?',
    answer: <div className="space-y-4" data-id="element-625">
            <p className="font-bold text-brand-light" data-id="element-626">
              Taille du tatouage et réalité du traitement
            </p>
            <p data-id="element-627">Absolument pas !</p>
            <p data-id="element-628">
              Contrairement à ce que l'on pourrait penser, la taille du tatouage
              n'est pas le facteur le plus déterminant.
            </p>
            <p data-id="element-629">Ce qui compte réellement, c'est :</p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-630">
              <li data-id="element-631">la densité de l'encre</li>
              <li data-id="element-632">sa profondeur dans la peau</li>
              <li data-id="element-633">la qualité des pigments utilisés</li>
            </ul>
            <p className="mt-4" data-id="element-634">
              Par exemple, un petit tatouage noir très profond peut nécessiter
              plus de séances qu'un grand tatouage ombré. Chaque tatouage est
              unique, et c'est pourquoi une évaluation personnalisée est
              toujours indispensable avant de commencer un détatouage.
            </p>
          </div>
  }, {
    question: 'Combien de temps dure une séance de détatouage ?',
    answer: <div className="space-y-4" data-id="element-635">
            <p className="font-bold text-brand-light" data-id="element-636">
              Déroulement et durée du traitement
            </p>
            <p data-id="element-637">
              Le détatouage laser est généralement bien plus rapide que la
              réalisation du tatouage lui-même. La durée dépend essentiellement
              de la surface à traiter.
            </p>
            <p data-id="element-638">Dans la majorité des cas :</p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-639">
              <li data-id="element-640">une séance dure entre 10 et 20 minutes</li>
              <li data-id="element-641">les séances dépassent rarement 30 minutes</li>
            </ul>
            <p className="mt-4" data-id="element-642">
              Cette rapidité permet d'intégrer facilement les séances dans un
              emploi du temps chargé, tout en conservant une prise en charge
              sérieuse et sécurisée.
            </p>
          </div>
  }]
}, {
  title: 'Douleur et confort',
  items: [{
    question: 'Le détatouage laser est-il douloureux ?',
    answer: <div className="space-y-4" data-id="element-643">
            <p className="font-bold text-brand-light" data-id="element-644">
              Sensations pendant le traitement
            </p>
            <p data-id="element-645">
              La sensation est souvent comparée à celle d'un élastique qui
              claque sur la peau. Le niveau d'inconfort dépend de la zone
              traitée et de la sensibilité individuelle.
            </p>
            <p data-id="element-646">Pour améliorer le confort :</p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-647">
              <li data-id="element-648">patchs froids</li>
              <li data-id="element-649">machine à air froid (cryo)</li>
              <li data-id="element-650">protocole de soins adapté</li>
            </ul>
            <p className="mt-4" data-id="element-651">
              La majorité des clients trouvent la douleur plus supportable
              qu'anticipé.
            </p>
          </div>
  }, {
    question: 'Peut-on utiliser une crème anesthésiante avant la séance ?',
    answer: <div className="space-y-4" data-id="element-652">
            <p className="font-bold text-brand-light" data-id="element-653">
              Anesthésie et réglementation suisse
            </p>
            <p data-id="element-654">
              Oui, il est possible d'utiliser une crème anesthésiante avant une
              séance de détatouage laser, mais uniquement sur prescription
              médicale. En Suisse, la législation est claire : seul un médecin
              est autorisé à prescrire un anesthésiant local, comme la crème
              Emla.
            </p>
            <p data-id="element-655">
              Si vous êtes inquiet(e) à l'idée de la douleur, c'est une
              préoccupation tout à fait compréhensible. Dans ce cas, vous pouvez
              en parler librement à votre médecin traitant et lui demander une
              ordonnance pour la crème Emla, en précisant que vous allez
              bénéficier d'un traitement laser.
            </p>
            <p data-id="element-656">
              Une fois la prescription obtenue, la crème devra être appliquée
              strictement selon les instructions du médecin, avant la séance de
              détatouage. Il est très important de respecter les temps de pose,
              les quantités et les consignes indiquées, afin d'éviter tout
              risque de complications ou de réactions cutanées.
            </p>
            <p data-id="element-657">
              Je reste bien entendu à votre écoute pour vous accompagner,
              répondre à vos questions et s'assurer que votre séance de
              détatouage sans cicatrice se déroule dans les meilleures
              conditions possibles, en toute sécurité et avec le maximum de
              confort possible.
            </p>
          </div>
  }]
}, {
  title: 'Prix et consultation',
  items: [{
    question: "Prix d'un détatouage laser à Martigny",
    answer: <div className="space-y-4" data-id="element-658">
            <p className="font-bold text-brand-light" data-id="element-659">
              Combien coûte un détatouage ?
            </p>
            <p data-id="element-660">
              Chez Dispel your tattoo, chaque traitement de détatouage est
              personnalisé. Le prix dépend de la taille du tatouage. Les tarifs
              débutent à partir de 60 CHF.
            </p>
            <p data-id="element-661">
              Un devis gratuit et personnalisé est proposé avant toute prise en
              charge. L'utilisation d'un équipement laser certifié CE garantit
              un détatouage sécurisé.
            </p>
          </div>
  }]
}, {
  title: 'Sécurité et risques',
  items: [{
    question: 'Qu\'est-ce que le blanchiment ou "frosting" après le laser ?',
    answer: <div className="space-y-4" data-id="element-662">
            <p className="font-bold text-brand-light" data-id="element-663">
              Une réaction normale et rassurante
            </p>
            <p data-id="element-664">
              Le frosting, aussi appelé blanchiment, est une réaction immédiate
              et normale après le passage du laser. Il apparaît lorsque les
              pigments éclatent et forment de micro-bulles de gaz qui remontent
              à la surface de la peau.
            </p>
            <p data-id="element-665">
              Même si cet effet peut surprendre lors de la première séance, il
              est en réalité très positif : il indique que le laser agit
              efficacement sur l'encre.
            </p>
            <p data-id="element-666">
              Le frosting est temporaire et disparaît généralement en 5 à 10
              minutes. Il est important de savoir que toutes les couleurs ne
              provoquent pas ce phénomène, car elles ne réagissent pas toutes de
              la même manière au laser.
            </p>
          </div>
  }, {
    question: 'Détatouage sans cicatrice : quels sont les risques ?',
    answer: <div className="space-y-4" data-id="element-667">
            <p className="font-bold text-brand-light" data-id="element-668">
              Transparence et sécurité du traitement
            </p>
            <p data-id="element-669">
              Le détatouage laser est considéré comme un traitement sûr
              lorsqu'il est correctement réalisé et encadré. Comme tout acte
              impliquant la peau, il comporte néanmoins certains risques, qu'il
              est important de connaître :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-670">
              <li data-id="element-671">risque d'infection en cas de non-respect des soins</li>
              <li data-id="element-672">cicatrices hypertrophiques ou chéloïdes (rares)</li>
              <li data-id="element-673">modifications temporaires de la pigmentation</li>
              <li data-id="element-674">douleur variable selon les personnes</li>
              <li data-id="element-675">efficacité partielle sur certains pigments</li>
            </ul>
            <p className="mt-4" data-id="element-676">
              La bonne nouvelle est que ces risques sont fortement réduits
              lorsque les consignes post-traitement sont suivies avec sérieux.
              Notre objectif est toujours de privilégier un détatouage sans
              cicatrice, progressif et respectueux de votre peau.
            </p>
          </div>
  }, {
    question: 'Le détatouage laisse-t-il des cicatrices ?',
    answer: <div className="space-y-4" data-id="element-677">
            <p className="font-bold text-brand-light" data-id="element-678">
              Cicatrisation et prévention
            </p>
            <p data-id="element-679">
              Lorsqu'il est bien réalisé et que les soins sont respectés, le
              détatouage laser vise un détatouage sans cicatrice.
            </p>
            <p data-id="element-680">
              Les cicatrices permanentes sont rares. Elles surviennent le plus
              souvent en cas :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-681">
              <li data-id="element-682">d'infection</li>
              <li data-id="element-683">d'exposition au soleil trop précoce</li>
              <li data-id="element-684">de non-respect des soins post-laser</li>
            </ul>
            <p className="mt-4" data-id="element-685">
              Un accompagnement sérieux et une bonne communication permettent de
              limiter ces risques au maximum.
            </p>
          </div>
  }]
}, {
  title: 'Cas spécifiques',
  items: [{
    question: 'Pouvez-vous enlever le maquillage permanent des sourcils ?',
    answer: <div className="space-y-4" data-id="element-686">
            <p className="font-bold text-brand-light" data-id="element-687">
              Détatouage sourcils : ce qu'il faut savoir
            </p>
            <p data-id="element-688">
              Oui, le détatouage sourcils au laser est possible et constitue
              aujourd'hui la méthode la plus sûre pour retirer un maquillage
              permanent. Cependant, certains pigments utilisés pour les sourcils
              contiennent des composants ferriques. Sous l'effet du laser, ces
              pigments peuvent s'oxyder et foncer temporairement, ou prendre une
              teinte rouge, orangée ou grise.
            </p>
            <p data-id="element-689">
              Cette réaction peut être impressionnante, mais elle est connue et
              maîtrisée. Les pigments restent traitables, même si cela peut
              nécessiter plus de séances et un protocole adapté.
            </p>
          </div>
  }, {
    question: 'Combien de temps attendre avant de faire un nouveau tatouage ?',
    answer: <div className="space-y-4" data-id="element-690">
            <p className="font-bold text-brand-light" data-id="element-691">
              Laisser le temps à la peau de se régénérer
            </p>
            <p data-id="element-692">
              Après un détatouage, la peau a besoin de temps pour se réparer en
              profondeur. Il est recommandé d'attendre au minimum 3 mois après
              la dernière séance avant de réaliser un nouveau tatouage sur la
              même zone.
            </p>
            <p data-id="element-693">Ce délai permet :</p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-694">
              <li data-id="element-695">une régénération complète de la peau</li>
              <li data-id="element-696">une meilleure tenue du futur tatouage</li>
              <li data-id="element-697">
                une réduction du risque de cicatrice ou de mauvaise
                cicatrisation
              </li>
            </ul>
            <p className="mt-4" data-id="element-698">
              Il est également conseillé d'échanger avec votre tatoueur sur
              l'état de la peau avant tout nouveau projet.
            </p>
          </div>
  }, {
    question: 'Détatouage ou recouvrement : que choisir ?',
    answer: <div className="space-y-4" data-id="element-699">
            <p className="font-bold text-brand-light" data-id="element-700">
              Comparaison des deux solutions pour modifier ou effacer un
              tatouage
            </p>
            <p data-id="element-701">
              Le détatouage et le recouvrement (cover) sont deux approches
              différentes pour se séparer d'un tatouage que l'on ne souhaite
              plus. Le choix dépend avant tout de votre objectif final, de vos
              attentes et de votre situation personnelle.
            </p>
            <p data-id="element-702">
              Le détatouage permet d'effacer un tatouage progressivement,
              jusqu'à sa disparition complète ou partielle, tandis que le
              recouvrement consiste à tatouer par-dessus le motif existant pour
              le masquer.
            </p>

            <p className="font-bold text-brand-light mt-6" data-id="element-703">
              Avantages du détatouage par rapport au recouvrement
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-704">
              <li data-id="element-705">
                Le détatouage est une méthode progressive et plus naturelle,
                sans ajout de nouvelle encre.
              </li>
              <li data-id="element-706">
                Il permet de supprimer réellement le tatouage, alors que le
                recouvrement ne fait que le dissimuler.
              </li>
              <li data-id="element-707">
                Il offre la possibilité de retrouver une peau vierge ou de
                préparer la zone pour un futur tatouage plus propre et plus
                précis.
              </li>
            </ul>

            <p className="font-bold text-brand-light mt-6" data-id="element-708">
              Inconvénients du détatouage par rapport au recouvrement
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-709">
              <li data-id="element-710">
                Le détatouage peut être plus coûteux qu'un recouvrement, en
                particulier lorsque plusieurs séances sont nécessaires pour
                atteindre le résultat souhaité.
              </li>
              <li data-id="element-711">
                Il peut être perçu comme plus douloureux qu'un cover, selon la
                sensibilité de la personne et la zone traitée.
              </li>
              <li data-id="element-712">
                Dans de rares cas, le détatouage peut laisser des marques,
                notamment en fonction de la taille, de la couleur, de la
                profondeur du tatouage et de la réaction individuelle de la
                peau.
              </li>
            </ul>

            <p className="font-bold text-brand-light mt-6" data-id="element-713">
              Faire le bon choix
            </p>
            <p data-id="element-714">
              En fin de compte, le choix entre le détatouage et le recouvrement
              dépend de plusieurs facteurs :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-715">
              <li data-id="element-716">vos préférences personnelles</li>
              <li data-id="element-717">votre budget</li>
              <li data-id="element-718">la taille et la complexité du tatouage</li>
              <li data-id="element-719">votre sensibilité à la douleur</li>
              <li data-id="element-720">votre capacité de cicatrisation</li>
            </ul>
            <p className="mt-4" data-id="element-721">
              Un échange approfondi et une évaluation personnalisée permettent
              de déterminer la solution la plus adaptée à votre situation, dans
              le respect de votre peau et de vos attentes.
            </p>
          </div>
  }]
}, {
  title: 'Préparation et soins',
  items: [{
    question: 'Quelles sont les précautions à prendre avant et après une séance de détatouage ?',
    answer: <div className="space-y-4" data-id="element-722">
            <p className="font-bold text-brand-light" data-id="element-723">
              Des gestes simples pour favoriser une bonne cicatrisation
            </p>
            <p data-id="element-724">
              Le succès d'un détatouage laser ne dépend pas uniquement du
              traitement lui-même, mais aussi du respect de certaines
              précautions avant et après la séance. Ces recommandations visent
              avant tout à protéger votre peau, à limiter les effets secondaires
              et à favoriser une cicatrisation optimale.
            </p>

            <p className="font-bold text-brand-light mt-6" data-id="element-725">
              Avant la séance de détatouage
            </p>
            <p data-id="element-726">
              Avant votre séance, quelques gestes simples permettent de préparer
              la peau dans de bonnes conditions :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-727">
              <li data-id="element-728">
                Évitez toute exposition au soleil ou aux UV pendant au moins
                deux semaines avant la séance. Une peau bronzée ou sensibilisée
                réagit moins bien au laser.
              </li>
              <li data-id="element-729">
                Évitez, les médicaments pouvant augmenter le risque de
                saignement, comme l'aspirine ou les anti-inflammatoires non
                stéroïdiens (AINS), au moins une semaine avant la séance (voir
                anamnèse et contre-indications).
              </li>
              <li data-id="element-730">
                Hydratez bien votre peau en buvant suffisamment d'eau dans les
                jours précédant le traitement. Une peau bien hydratée cicatrise
                généralement mieux.
              </li>
            </ul>

            <p className="font-bold text-brand-light mt-6" data-id="element-731">
              Après la séance de détatouage
            </p>
            <p data-id="element-732">
              Après la séance, la peau a besoin de soins attentifs pour guérir
              correctement. Ces étapes sont essentielles pour réduire les
              risques et favoriser un détatouage sans cicatrice.
            </p>
            <ul className="list-disc pl-5 space-y-2 text-brand-gray" data-id="element-733">
              <li data-id="element-734">
                Pendant 5 à 10 jours, appliquez une fine couche de crème
                désinfectante et cicatrisante (comme Bepanthen Plus) 2 à 3 fois
                par jour. Lorsque cela est possible, laissez la zone respirer ;
                sinon, protégez-la avec une gaze stérile. Une fois cette
                première phase passée, poursuivez avec une crème régénérative
                (Cicalfate d'Avène ou Cicaplast de La Roche-Posay) jusqu'à
                cicatrisation complète.
              </li>
              <li data-id="element-735">
                Appliquez du froid sur la zone traitée plusieurs fois par jour
                pendant 2 à 3 jours. Il est important d'utiliser un froid
                contrôlé entre 10 et 15 °C, et non de la glace directement sur
                la peau, afin de limiter le risque d'œdème sans perturber le
                processus inflammatoire naturel, indispensable à une bonne
                cicatrisation.
              </li>
              <li data-id="element-736">
                Pendant 10 à 15 jours après la séance, évitez les expositions
                prolongées à l'humidité (bains, piscine) ainsi que la
                transpiration excessive (sport, sauna), en fonction de
                l'évolution de la cicatrisation.
              </li>
              <li data-id="element-737">
                Pendant 30 jours, n'exposez pas la zone traitée au soleil ni à
                d'autres formes de rayonnement, comme les solariums.
              </li>
              <li data-id="element-738">
                Enfin, il est essentiel de ne jamais arracher ni accélérer la
                chute des éventuelles cloques ou croûtes. Elles font partie du
                processus naturel de guérison.
              </li>
            </ul>
            <p className="mt-4" data-id="element-739">
              En respectant ces recommandations, vous mettez toutes les chances
              de votre côté pour une récupération sereine et un résultat
              optimal.
            </p>
          </div>
  }]
}, {
  title: 'Contre-indications',
  items: [{
    question: 'Quelles sont les contre-indications pour le traitement de détatouage laser ?',
    answer: <div className="space-y-4" data-id="element-740">
            <p className="font-bold text-brand-light" data-id="element-741">
              Situations nécessitant un certificat médical
            </p>
            <p data-id="element-742">
              Le détatouage laser est un traitement encadré qui vise à respecter
              au maximum la santé de votre peau et votre bien-être général. Dans
              certaines situations, un certificat d'aptitude délivré par votre
              médecin traitant est nécessaire avant de pouvoir débuter un
              traitement.
            </p>
            <p data-id="element-743">
              Cette démarche ne signifie pas que le détatouage est
              automatiquement contre-indiqué, mais qu'une évaluation médicale
              préalable est indispensable afin d'adapter le traitement ou, si
              besoin, de le différer en toute sécurité.
            </p>
            <p data-id="element-744">
              Un certificat médical est généralement requis dans les situations
              suivantes (liste non exhaustive) :
            </p>
            <ul className="list-disc pl-5 space-y-1 text-brand-gray" data-id="element-745">
              <li data-id="element-746">affections de la glande surrénale ou des reins</li>
              <li data-id="element-747">maladies de la peau (herpès, psoriasis, eczéma, etc.)</li>
              <li data-id="element-748">
                allergies au soleil ou manifestations allergiques cutanées
              </li>
              <li data-id="element-749">
                maladies immunitaires ou diminution du système immunitaire
              </li>
              <li data-id="element-750">maladies infectieuses ou infections en cours</li>
              <li data-id="element-751">
                troubles de la coagulation sanguine (thrombose, phlébite,
                embolie, caillot)
              </li>
              <li data-id="element-752">déséquilibres hormonaux</li>
              <li data-id="element-753">maladies du système nerveux, comme l'épilepsie</li>
              <li data-id="element-754">maladies endocriniennes, notamment le diabète</li>
              <li data-id="element-755">
                maladies cardiaques, hypertension importante ou port d'un
                pacemaker
              </li>
              <li data-id="element-756">cancer</li>
              <li data-id="element-757">grossesse et allaitement</li>
              <li data-id="element-758">cicatrices chéloïdes ou hypertrophiques</li>
            </ul>
            <p className="mt-4" data-id="element-759">
              L'objectif de cette précaution est avant tout de garantir un
              détatouage sûr, respectueux de votre état de santé et de votre
              capacité de cicatrisation. En cas de doute, le dialogue entre le
              praticien, le médecin et la personne traitée permet toujours de
              trouver la solution la plus adaptée.
            </p>
          </div>
  }]
}];
// --- Utils ---
function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return node.toString();
  if (!node) return '';
  if (Array.isArray(node)) return node.map(extractText).join(' ');
  if (isValidElement(node)) {
    return extractText(node.props.children);
  }
  return '';
}
const slugify = (text: string) => {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
};
// --- Components ---
const AccordionItem = ({
  item,
  isOpen,
  onClick
}: {
  item: FAQItem;
  isOpen: boolean;
  onClick: () => void;
}) => {
  return <div className="border-b border-brand-border/50 last:border-0" data-id="element-760">
      <button onClick={onClick} className="w-full max-w-[800px] py-7 md:py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-761">
        <h3 className="text-lg md:text-xl font-medium text-brand-light group-hover:text-white transition-colors pr-8" data-id="element-762">
          {item.question}
        </h3>
        <span className="flex-shrink-0 w-8 h-8 rounded-full border border-brand-border flex items-center justify-center text-brand-gray group-hover:border-brand-light group-hover:text-brand-light transition-all duration-300" data-id="element-763">
          <motion.div animate={{
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-764">
            {isOpen ? <Minus size={16} data-id="element-765" /> : <Plus size={16} data-id="element-766" />}
          </motion.div>
        </span>
      </button>
      <AnimatePresence initial={false} data-id="element-767">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1,
        transition: {
          height: {
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1]
          },
          opacity: {
            duration: 0.2,
            delay: 0.1
          }
        }
      }} exit={{
        height: 0,
        opacity: 0,
        transition: {
          height: {
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1]
          },
          opacity: {
            duration: 0.2
          }
        }
      }} className="overflow-hidden" data-id="element-768">
            <div className="pb-8 pt-2 text-brand-gray font-light leading-[1.85] text-base md:text-lg [&_.space-y-4]:space-y-5 [&_.space-y-1]:space-y-2" data-id="element-769">
              {item.answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const FAQ = () => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  useSEO({
    title: 'FAQ Détatouage Laser | Questions Fréquentes | Dispel Your Tattoo Martigny',
    description: 'Réponses à toutes vos questions sur le détatouage laser : fonctionnement, douleur, nombre de séances, prix, soins post-traitement. Informations complètes par un spécialiste en Valais.',
    canonical: 'https://www.dispelyourtattoo.ch/faq'
  });
  useEffect(() => {
    // Add FAQPage structured data
    const schemaId = 'faq-schema-ld';
    let existingSchema = document.getElementById(schemaId);
    if (existingSchema) existingSchema.remove();
    // Extract first 8 questions for schema
    const schemaQuestions = faqData.flatMap(cat => cat.items).slice(0, 8).map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: extractText(item.answer)
      }
    }));
    const schema = document.createElement('script');
    schema.id = schemaId;
    schema.type = 'application/ld+json';
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: schemaQuestions
    });
    document.head.appendChild(schema);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);
  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const handleScrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('contact-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleScrollToCategory = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  // Filter logic
  const filteredData = faqData.map(category => {
    if (!searchQuery.trim()) return category;
    const query = searchQuery.toLowerCase();
    const filteredItems = category.items.filter(item => {
      const questionMatch = item.question.toLowerCase().includes(query);
      const answerText = extractText(item.answer).toLowerCase();
      const answerMatch = answerText.includes(query);
      return questionMatch || answerMatch;
    });
    return {
      ...category,
      items: filteredItems
    };
  }).filter(category => category.items.length > 0);
  // Auto-expand when searching
  const isSearching = searchQuery.trim().length > 0;
  return <main className="pt-32 pb-0" data-id="element-770">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 max-w-[1100px] mx-auto text-center" data-id="element-771">
        <ScrollReveal data-id="element-772">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full border border-brand-border bg-brand-darker mb-8" data-id="element-773">
            <span className="w-2 h-2 rounded-full bg-brand-light" data-id="element-774"></span>
            <span className="text-xs font-medium uppercase tracking-widest text-brand-gray" data-id="element-775">
              Informations
            </span>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1} data-id="element-776">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-medium text-brand-light mb-8 tracking-tight text-balance" data-id="element-777">
            FAQ Détatouage Laser à Martigny
          </h1>
        </ScrollReveal>

        <ScrollReveal delay={0.2} data-id="element-778">
          <div className="text-lg md:text-xl text-brand-gray font-light leading-relaxed max-w-3xl mx-auto space-y-6" data-id="element-779">
            <p className="font-medium text-brand-light" data-id="element-780">
              Détatouage Martigny : FAQ complète sur le détatouage laser
            </p>
            <p data-id="element-781">
              Le détatouage laser est aujourd'hui la solution la plus efficace
              pour effacer un tatouage ou un maquillage permanent en toute
              sécurité. À Martigny, Dispel your tattoo vous permet d'effacer un
              tatouage en préservant l'intégrité de votre peau.
            </p>
            <p data-id="element-782">
              Cette FAQ vous apporte des réponses détaillées, claires et
              réalistes afin de vous aider à comprendre chaque étape du
              traitement.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.3} data-id="element-783">
          <button onClick={handleScrollToContact} className="mt-12 inline-flex items-center space-x-2 text-sm font-medium text-brand-light hover:text-white border-b border-brand-light/30 hover:border-brand-light pb-1 transition-all" data-id="element-784">
            <span data-id="element-785">Poser une question</span>
            <ArrowRight size={16} data-id="element-786" />
          </button>
        </ScrollReveal>
      </section>

      {/* Anchor Navigation + Search */}
      <div className="sticky top-[72px] md:top-[88px] z-40 bg-brand-darker/90 backdrop-blur-md border-y border-brand-border/30" data-id="element-787">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-3 md:py-4 space-y-3 md:space-y-4" data-id="element-788">
          {/* Category links - wrapping */}
          <div className="flex flex-wrap gap-x-4 md:gap-x-6 gap-y-2" data-id="element-789">
            {faqData.map((category, idx) => <a key={idx} href={`#faq-${slugify(category.title)}`} onClick={e => handleScrollToCategory(e, `faq-${slugify(category.title)}`)} className="text-[10px] md:text-xs lg:text-sm font-medium text-brand-gray hover:text-brand-light uppercase tracking-wider transition-colors" data-id="element-790">
                {category.title}
              </a>)}
          </div>
          {/* Inline Search */}
          <div className="relative" data-id="element-791">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-792">
              <Search className="h-4 w-4 text-brand-gray" data-id="element-793" />
            </div>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Rechercher une question…" className="w-full bg-brand-dark/80 border border-brand-border/40 rounded-full py-2 md:py-2.5 pl-11 pr-5 text-sm text-brand-light placeholder-brand-gray/50 focus:outline-none focus:border-brand-light/50 transition-all" data-id="element-794" />
          </div>
        </div>
      </div>

      {/* FAQ Accordion Section */}
      <section className="py-12 md:py-16 lg:py-24 bg-brand-darker" data-id="element-795">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12" data-id="element-796">
          {filteredData.length === 0 ? <div className="text-center py-12 text-brand-gray" data-id="element-797">
              Aucun résultat trouvé pour "{searchQuery}"
            </div> : filteredData.map((category, categoryIndex) => {
          // Find original index to maintain consistent state keys
          const originalCategoryIndex = faqData.findIndex(c => c.title === category.title);
          return <div key={categoryIndex} id={`faq-${slugify(category.title)}`} className="mb-12 md:mb-16 last:mb-0 scroll-mt-32 md:scroll-mt-40" data-id="element-798">
                  <ScrollReveal data-id="element-799">
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-medium tracking-tight text-brand-light mb-6 md:mb-8 pb-4 border-b border-brand-border max-w-[800px]" data-id="element-800">
                      {category.title}
                    </h2>
                  </ScrollReveal>

                  <div className="space-y-2" data-id="element-801">
                    {category.items.map((item, itemIndex) => {
                // Find original item index
                const originalItemIndex = faqData[originalCategoryIndex].items.findIndex(i => i.question === item.question);
                const isOpen = isSearching || !!openItems[`${originalCategoryIndex}-${originalItemIndex}`];
                return <ScrollReveal key={itemIndex} delay={itemIndex * 0.05} data-id="element-802">
                          <AccordionItem item={item} isOpen={isOpen} onClick={() => {
                    if (!isSearching) {
                      toggleItem(originalCategoryIndex, originalItemIndex);
                    }
                  }} data-id="element-803" />
                        </ScrollReveal>;
              })}
                  </div>
                </div>;
        })}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-32 bg-brand-dark" data-id="element-804">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12" data-id="element-805">
          <ScrollReveal data-id="element-806">
            <div className="text-center mb-16" data-id="element-807">
              <h2 className="text-3xl md:text-4xl font-medium text-brand-light mb-4 tracking-tight" data-id="element-808">
                Des réponses claires, une expérience rassurante
              </h2>
              <p className="text-brand-gray font-light" data-id="element-809">
                Ce que nos clients disent de leur prise en charge.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-810">
            <ScrollReveal delay={0.1} data-id="element-811">
              <div className="p-8 rounded-2xl bg-brand-darker border border-brand-border/50 h-full" data-id="element-812">
                <MessageSquare className="text-brand-border mb-6" size={32} data-id="element-813" />
                <p className="text-brand-gray font-light leading-relaxed mb-6 italic" data-id="element-814">
                  "J'étais très angoissée par la douleur et les cicatrices. Les
                  explications claires et transparentes m'ont totalement
                  rassurée avant ma première séance."
                </p>
                <p className="text-brand-light font-medium text-sm" data-id="element-815">
                  — Sophie M.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-816">
              <div className="p-8 rounded-2xl bg-brand-darker border border-brand-border/50 h-full" data-id="element-817">
                <ShieldCheck className="text-brand-border mb-6" size={32} data-id="element-818" />
                <p className="text-brand-gray font-light leading-relaxed mb-6 italic" data-id="element-819">
                  "Une approche médicale et professionnelle. On ne m'a pas vendu
                  du rêve, mais des résultats réalistes. C'est exactement ce que
                  je cherchais."
                </p>
                <p className="text-brand-light font-medium text-sm" data-id="element-820">
                  — Laurent D.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-821">
              <div className="p-8 rounded-2xl bg-brand-darker border border-brand-border/50 h-full" data-id="element-822">
                <Clock className="text-brand-border mb-6" size={32} data-id="element-823" />
                <p className="text-brand-gray font-light leading-relaxed mb-6 italic" data-id="element-824">
                  "Le suivi post-séance est impeccable. Savoir exactement à quoi
                  s'attendre et comment soigner sa peau fait toute la différence
                  dans le processus."
                </p>
                <p className="text-brand-light font-medium text-sm" data-id="element-825">
                  — Camille T.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-brand-darker border-y border-brand-border/30 text-center relative overflow-hidden" data-id="element-826">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-brand-light/5 blur-[120px] rounded-full pointer-events-none" data-id="element-827"></div>

        <div className="relative z-10 max-w-3xl mx-auto px-6" data-id="element-828">
          <ScrollReveal data-id="element-829">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-brand-light mb-6 tracking-tight" data-id="element-830">
              Vous avez encore des questions ?
            </h2>
            <p className="text-lg md:text-xl text-brand-gray font-light mb-10 md:mb-12" data-id="element-831">
              Chaque tatouage est unique. N'hésitez pas à nous contacter pour
              obtenir des conseils personnalisés adaptés à votre situation.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-832">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" data-id="element-833">
              <a href="/#contact" className="w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 bg-brand-light text-brand-dark rounded-full font-bold uppercase tracking-wider hover:bg-white transition-colors duration-300 text-sm md:text-base" data-id="element-834">
                Réserver une consultation
              </a>
              <button onClick={handleScrollToContact} className="w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 border border-brand-border text-brand-light rounded-full font-bold uppercase tracking-wider hover:border-brand-light transition-colors duration-300 text-sm md:text-base" data-id="element-835">
                Nous contacter
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="py-24 bg-brand-dark" data-id="element-836">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 text-center" data-id="element-837">
          <ScrollReveal data-id="element-838">
            <h2 className="text-2xl font-medium text-brand-light mb-4 tracking-tight" data-id="element-839">
              Notre équipe est disponible pour répondre à toutes vos questions
            </h2>
            <div className="w-16 h-[1px] bg-brand-border mx-auto mb-12" data-id="element-840"></div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-841">
            <ScrollReveal delay={0.1} data-id="element-842">
              <a href="tel:+41791026945" className="flex flex-col items-center p-8 rounded-2xl hover:bg-brand-darker transition-colors group" data-id="element-843">
                <div className="w-12 h-12 rounded-full border border-brand-border flex items-center justify-center mb-6 group-hover:border-brand-light group-hover:text-brand-light text-brand-gray transition-colors" data-id="element-844">
                  <Phone size={20} data-id="element-845" />
                </div>
                <h3 className="text-brand-light font-medium mb-2" data-id="element-846">Téléphone</h3>
                <p className="text-brand-gray font-light" data-id="element-847">079 102 69 45</p>
              </a>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-848">
              <a href="mailto:dispeltattoo@gmail.com" className="flex flex-col items-center p-8 rounded-2xl hover:bg-brand-darker transition-colors group" data-id="element-849">
                <div className="w-12 h-12 rounded-full border border-brand-border flex items-center justify-center mb-6 group-hover:border-brand-light group-hover:text-brand-light text-brand-gray transition-colors" data-id="element-850">
                  <Mail size={20} data-id="element-851" />
                </div>
                <h3 className="text-brand-light font-medium mb-2" data-id="element-852">Email</h3>
                <p className="text-brand-gray font-light" data-id="element-853">
                  dispeltattoo@gmail.com
                </p>
              </a>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-854">
              <a href="https://share.google/HfzVyKv5w71opN00D" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-8 rounded-2xl hover:bg-brand-darker transition-colors group" data-id="element-855">
                <div className="w-12 h-12 rounded-full border border-brand-border flex items-center justify-center mb-6 group-hover:border-brand-light group-hover:text-brand-light text-brand-gray transition-colors" data-id="element-856">
                  <MapPin size={20} data-id="element-857" />
                </div>
                <h3 className="text-brand-light font-medium mb-2" data-id="element-858">Adresse</h3>
                <p className="text-brand-gray font-light" data-id="element-859">
                  Rue de la Délèze 2<br data-id="element-860" />
                  1920 Martigny
                </p>
              </a>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <NextPageLink data-id="element-861" />
    </main>;
};